/**
 * Farbrollen, Formatierung und Datenaufbereitung des Lieferverzug-Reiters.
 *
 * Alles hier ist REIN (keine React-Abhaengigkeit, kein DOM) — damit die
 * Aufbereitung testbar bleibt und die Diagramme nur noch zeichnen.
 *
 * FARBEN
 * ------------------------------------------------------------------
 * Die Verspaetungsklassen bedeuten gut→schlecht, also tragen sie
 * Status-Farben (gruen/gelb/orange/rot) und nie Serien-Farben. Serien, die
 * blosse Identitaet tragen (Zusagetreue vs. Lieferantentreue), tragen die
 * geprueften kategorialen Slots 1 (blau) und 2 (orange).
 *
 * Geprueft mit dem dataviz-Validator gegen die weisse Kartenflaeche:
 * blau/orange bestehen alle sechs Checks (CVD ΔE 24,7 / normal 33,6, beide
 * ueber 3:1 Kontrast). `warnung` (1,83:1) und `ernst` (2,64:1) liegen bewusst
 * unter 3:1 — Ausgleich sind Legende, Beschriftung und die Tabellen-Ansicht,
 * die jede Karte umschalten kann. Farbe traegt hier nie allein die Aussage.
 */

import type {
  AnalyticsFall, GruppenQuote, PrognosePunkt, Quote,
  VerschiebungPunkt,
} from './typen';

// ─── Farbrollen ──────────────────────────────────────────────────────────────

export const FARBEN = {
  flaeche: '#FFFFFF',
  seite: '#FAFAF8',
  tinteStark: '#1A1A1A',     // CI-Schwarz
  tinteMittel: '#52514E',
  tinteLeise: '#898781',     // Achsen/Beschriftung
  raster: '#E1E0D9',         // Hairline, immer durchgezogen
  linie: '#DAD9D9',          // CI-Grau, Kartenrand
  akzent: '#E9FF57',         // CI-Gelb — nur Chrome (Reiter, Marke), nie Datenfarbe
  // Status = Verspaetungsklassen
  gut: '#0CA30C',
  warnung: '#FAB219',
  ernst: '#EC835A',
  kritisch: '#D03B3B',
  // Kategorial (Identitaet)
  serie1: '#2A78D6',         // Zusagetreue (Leitzahl)
  serie2: '#EB6834',         // Lieferantentreue (Vergleich)
  serie1Hell: '#9EC5F4',     // Planbalken — heller Schritt derselben Rampe
  serie1Dunkel: '#184F95',   // Erwartungslinie
  band: '#CDE2FB',           // Prognoseband (Wasch, ~10 %)
} as const;

/** Verspaetungsklassen in fester Reihenfolge — Legende und Stapel nutzen dieselbe. */
export const KLASSEN = [
  { key: 'puenktlich', label: 'pünktlich', farbe: FARBEN.gut },
  { key: 'spaet1', label: '1 Woche später', farbe: FARBEN.warnung },
  { key: 'spaet2bis3', label: '2–3 Wochen später', farbe: FARBEN.ernst },
  { key: 'spaet4plus', label: '4+ Wochen später', farbe: FARBEN.kritisch },
] as const;

// ─── Formatierung (deutsch) ──────────────────────────────────────────────────

/** "2026-KW34" → "KW 34" (die Jahreszahl steht in der Achsen-Beschriftung). */
export function kwKurz(woche: string): string {
  const m = /^(\d{4})-KW(\d{2})$/.exec(woche);
  return m ? `KW ${Number(m[2])}` : woche;
}

/** "2026-KW34" → "KW 34/2026" fuer Tooltip und Tabelle. */
export function kwLang(woche: string): string {
  const m = /^(\d{4})-KW(\d{2})$/.exec(woche);
  return m ? `KW ${Number(m[2])}/${m[1]}` : woche;
}

const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

/** "2026-08" → "Aug. 2026". */
export function monatKurz(monat: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(monat);
  if (!m) return monat;
  const name = MONATE[Number(m[2]) - 1] ?? monat;
  return `${name.slice(0, 3)}. ${m[1]}`;
}

/** Anteil 0..1 als deutsche Prozentangabe; null → "—". */
export function prozent(anteil: number | null | undefined, nachkomma = 0): string {
  if (anteil === null || anteil === undefined || Number.isNaN(anteil)) return '—';
  return `${(anteil * 100).toLocaleString('de-DE', {
    minimumFractionDigits: nachkomma, maximumFractionDigits: nachkomma,
  })} %`;
}

export function zahl(wert: number | null | undefined): string {
  if (wert === null || wert === undefined || Number.isNaN(wert)) return '—';
  return wert.toLocaleString('de-DE', { maximumFractionDigits: 1 });
}

/** ISO-Datum oder -Zeitstempel → "17.08.2026". */
export function datumKurz(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Eine Quote als Satz — genau so steht sie im Tooltip und unter der Leitzahl.
 * Die Fallzahl ist Pflichtbestandteil: eine Prozentzahl ohne "von wie vielen"
 * ist auf einer Management-Folie nicht bewertbar.
 */
export function quoteText(q: Quote | null | undefined): string {
  if (!q || q.anteil === null) return 'noch keine abgeschlossenen Fälle';
  const basis = `${prozent(q.anteil)} von ${q.n} ${q.n === 1 ? 'Auftrag' : 'Aufträgen'}`;
  const spanne = q.unten !== null && q.oben !== null && q.n > 1
    ? ` · Schwankungsbereich ${prozent(q.unten)}–${prozent(q.oben)}`
    : '';
  return `${basis}${spanne}`;
}

// ─── Kalenderwochen-Arithmetik (ISO) ─────────────────────────────────────────
// Nur fuers Auffuellen von Achsenluecken — die fachliche Wochenrechnung lebt
// im Backend (verlauf.ts). Wochen ohne Eintrag muessen auf der Achse trotzdem
// erscheinen, sonst sieht KW 24 → KW 26 wie eine Woche Abstand aus.

/** Montag der ISO-Woche "2026-KW34" als UTC-Datum. */
export function kwMontag(woche: string): Date | null {
  const m = /^(\d{4})-KW(\d{2})$/.exec(woche);
  if (!m) return null;
  const jahr = Number(m[1]);
  const kw = Number(m[2]);
  // 4. Januar liegt immer in KW 1 (ISO 8601).
  const vierterJanuar = new Date(Date.UTC(jahr, 0, 4));
  const wochentag = (vierterJanuar.getUTCDay() + 6) % 7; // 0 = Montag
  const montagKw1 = new Date(vierterJanuar);
  montagKw1.setUTCDate(vierterJanuar.getUTCDate() - wochentag);
  const montag = new Date(montagKw1);
  montag.setUTCDate(montagKw1.getUTCDate() + (kw - 1) * 7);
  return montag;
}

/** ISO-Woche eines UTC-Datums als "2026-KW34". */
export function wocheVon(datum: Date): string {
  const d = new Date(Date.UTC(datum.getUTCFullYear(), datum.getUTCMonth(), datum.getUTCDate()));
  d.setUTCDate(d.getUTCDate() + 4 - ((d.getUTCDay() + 6) % 7 + 1)); // Donnerstag der Woche
  const jahr = d.getUTCFullYear();
  const erster = new Date(Date.UTC(jahr, 0, 1));
  const kw = Math.ceil(((d.getTime() - erster.getTime()) / 86400000 + 1) / 7);
  return `${jahr}-KW${String(kw).padStart(2, '0')}`;
}

/** Die auf "2026-KW52" folgende Woche — inklusive Jahreswechsel. */
export function naechsteWoche(woche: string): string {
  const montag = kwMontag(woche);
  if (!montag) return woche;
  montag.setUTCDate(montag.getUTCDate() + 7);
  return wocheVon(montag);
}

/**
 * Fuellt Luecken in einer nach Woche sortierten Reihe mit Leerzeilen auf,
 * damit die X-Achse echte Zeit zeigt statt "vorhandene Eintraege nebeneinander".
 */
export function fuelleWochenluecken<T extends { woche: string }>(
  zeilen: T[],
  leer: (woche: string) => T,
  maxLuecken = 120,
): T[] {
  if (zeilen.length < 2) return zeilen;
  const out: T[] = [zeilen[0]];
  for (let i = 1; i < zeilen.length; i++) {
    let cursor = zeilen[i - 1].woche;
    let schutz = 0;
    while (schutz++ < maxLuecken) {
      cursor = naechsteWoche(cursor);
      if (cursor >= zeilen[i].woche || cursor === zeilen[i - 1].woche) break;
      out.push(leer(cursor));
    }
    out.push(zeilen[i]);
  }
  return out;
}

// ─── Datenaufbereitung fuer die Diagramme ────────────────────────────────────

export interface PrognoseZeile extends PrognosePunkt {
  label: string;
  /** Recharts zeichnet ein Band aus einem Wertepaar [unten, oben]. */
  band: [number, number];
  /** Differenz Plan − Erwartung; das ist die Zahl, die im Termin fehlt. */
  luecke: number;
}

export function bauePrognoseZeilen(prognose: PrognosePunkt[]): PrognoseZeile[] {
  return prognose.map((p) => ({
    ...p,
    label: kwKurz(p.woche),
    band: [p.bandUnten, p.bandOben],
    luecke: Math.max(0, p.plan - p.erwartet),
  }));
}

/** Naechste runde Schrittweite fuer eine Achse. */
export function achsenSchritt(spanne: number): number {
  const roh = Math.max(spanne, 1) / 4;
  const kandidaten = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500];
  return kandidaten.find((k) => k >= roh) ?? Math.ceil(roh / 1000) * 1000;
}

export interface VerschiebungAchse {
  unten: number;
  oben: number;
  ticks: number[];
}

/**
 * Y-Achse des Verschiebungs-Diagramms.
 *
 * Beide Richtungen teilen sich EINE lineare Skala — ein Balken "3 nach vorne"
 * ist damit genauso lang wie "3 nach hinten". Der Ausschnitt wird aber NICHT
 * kuenstlich symmetrisch gemacht: wenn 24 Aufträge nach hinten und nur 6 nach
 * vorne wandern, bliebe die untere Haelfte leer und die Vorziehungen waeren
 * kaum noch zu sehen.
 */
export function verschiebungAchse(zeilen: VerschiebungZeile[]): VerschiebungAchse {
  const maxHinten = zeilen.reduce((m, z) => Math.max(m, z.nachHinten), 0);
  const maxVorne = zeilen.reduce((m, z) => Math.max(m, z.nachVorne), 0);
  const schritt = achsenSchritt(Math.max(maxHinten, maxVorne));
  const oben = Math.max(schritt, Math.ceil(maxHinten / schritt) * schritt);
  const unten = maxVorne === 0 ? 0 : -Math.ceil(maxVorne / schritt) * schritt;

  const ticks: number[] = [];
  for (let w = unten; w <= oben; w += schritt) ticks.push(w);
  if (!ticks.includes(0)) ticks.push(0);
  return { unten, oben, ticks: ticks.sort((a, b) => a - b) };
}

/**
 * Gruppen (Artikel/Hersteller) fuer die Tabelle: nur belastbare Zeilen nach
 * oben, `duenn` bleibt sichtbar statt weggefiltert zu werden.
 */
export function sortiereGruppen(gruppen: GruppenQuote[], maxZeilen = 20): GruppenQuote[] {
  return [...gruppen]
    .sort((a, b) => {
      // Erst die belastbaren, darin nach Quote, dann nach Fallzahl.
      if (a.quote.duenn !== b.quote.duenn) return a.quote.duenn ? 1 : -1;
      return (b.quote.anteil ?? 0) - (a.quote.anteil ?? 0) || b.auftraege - a.auftraege;
    })
    .slice(0, maxZeilen);
}

/**
 * Kurzbewertung der Leitzahl fuer die Kopfzeile. Bewusst grob und mit dem
 * Hinweis auf duenne Basis — eine Ampel aus 6 Faellen waere eine Behauptung.
 */
export function bewerteLeitzahl(q: Quote): { text: string; farbe: string } {
  if (q.anteil === null) return { text: 'noch keine abgeschlossenen Fälle', farbe: FARBEN.tinteLeise };
  if (q.duenn) return { text: 'erst wenige Fälle — nur als Tendenz lesen', farbe: FARBEN.tinteMittel };
  if (q.anteil >= 0.8) return { text: 'Termine werden überwiegend gehalten', farbe: FARBEN.gut };
  if (q.anteil >= 0.6) return { text: 'etwa jeder dritte Termin rutscht', farbe: FARBEN.warnung };
  return { text: 'die Mehrheit der Termine hält nicht', farbe: FARBEN.kritisch };
}

// ─── Gruppen-Filter (Hersteller/Artikel) ─────────────────────────────────────

export interface GeteilteGruppen {
  sichtbar: GruppenQuote[];
  /** Wie viele Zeilen wegen zu weniger Faelle ausgeblendet wurden. */
  ausgeblendet: number;
}

/**
 * Blendet Gruppen mit zu wenigen abgeschlossenen Faellen aus.
 *
 * Anlass (Matze, 18.08.): in der Hersteller-Tabelle stand HUCK mit "100 %
 * verspaetet" — aus EINEM einzigen Auftrag. So eine Zeile sieht aus wie ein
 * Problemfall und ist keiner. Ausgeblendete Zeilen werden gezaehlt und als
 * Fussnote ausgewiesen — verschwinden lassen ohne Hinweis waere gelogen.
 */
export function teileGruppen(gruppen: GruppenQuote[], minFaelle: number): GeteilteGruppen {
  const sichtbar = gruppen.filter((g) => g.auftraege >= minFaelle);
  return { sichtbar, ausgeblendet: gruppen.length - sichtbar.length };
}

// ─── "Kurz gesagt"-Saetze ────────────────────────────────────────────────────
// Jede Karte bekommt einen automatisch gebauten Klartext-Satz. Die Regel ist
// streng: nur sagen, was die Zahlen hergeben — keine Bewertung erfinden, bei
// duenner Basis das dazusagen.

const anz = (n: number, singular: string, plural: string) =>
  `${zahl(n)} ${n === 1 ? singular : plural}`;

/** Fazit der Prognose-Karte: Summe Plan vs. Summe Erwartung. */
export function fazitPrognose(zeilen: PrognoseZeile[]): string | null {
  if (zeilen.length === 0) return null;
  const plan = zeilen.reduce((s, z) => s + z.plan, 0);
  const erwartet = zeilen.reduce((s, z) => s + z.erwartet, 0);
  const luecke = plan - erwartet;
  const zeitraum = zeilen.length === 1 ? `für ${kwLang(zeilen[0].woche)}` : `für die nächsten ${zeilen.length} Wochen`;
  if (luecke <= 0) {
    return `Der Lieferant hat ${zeitraum} ${anz(plan, 'Auftrag', 'Aufträge')} angekündigt — nach bisheriger Erfahrung ist damit auch zu rechnen.`;
  }
  return `Der Lieferant hat ${zeitraum} ${anz(plan, 'Auftrag', 'Aufträge')} angekündigt. `
    + `Nach bisheriger Erfahrung kommen davon etwa ${zahl(erwartet)} wirklich in der genannten Woche — `
    + `rund ${anz(luecke, 'Auftrag', 'Aufträge')} dürften sich weiter nach hinten schieben.`;
}

/** Fazit der Verschiebungs-Karte: Summen beider Richtungen. */
export function fazitVerschiebungen(zeilen: VerschiebungZeile[]): string | null {
  if (zeilen.length === 0) return null;
  const hinten = zeilen.reduce((s, z) => s + z.nachHinten, 0);
  const vorne = zeilen.reduce((s, z) => s + z.nachVorne, 0);
  if (hinten === 0 && vorne === 0) return 'Im gewählten Zeitraum wurde kein einziger Termin umgeplant.';
  const teile: string[] = [];
  if (hinten > 0) teile.push(`${anz(hinten, 'Termin', 'Termine')} nach hinten verschoben`);
  if (vorne > 0) teile.push(`${anz(vorne, 'Termin', 'Termine')} vorgezogen`);
  const verhaeltnis = hinten > 0 && vorne > 0 && hinten >= vorne * 3
    ? ' Verschiebungen gehen also fast immer nach hinten.'
    : '';
  return `Im gewählten Zeitraum wurden ${teile.join(' und ')}.${verhaeltnis}`;
}

/** Fazit der Wartezeit-Karte: erste vs. letzte Periode mit Messwert. */
export function fazitVorlauf(zeilen: VorlaufZeile[]): string | null {
  const mitWert = zeilen.filter((z): z is VorlaufZeile & { medianTage: number } => z.medianTage !== null);
  if (mitWert.length === 0) return null;
  const letzt = mitWert[mitWert.length - 1];
  const basis = `Zuletzt (${letzt.label}) lag die typische Wartezeit von der Auftragsbestätigung bis zum Versand bei ${anz(letzt.medianTage, 'Tag', 'Tagen')}`;
  if (mitWert.length === 1) return `${basis}.`;
  const erst = mitWert[0];
  const diff = letzt.medianTage - erst.medianTage;
  if (diff === 0) return `${basis} — genauso lange wie zu Beginn (${erst.label}).`;
  return `${basis} — ${anz(Math.abs(diff), 'Tag', 'Tage')} ${diff > 0 ? 'länger' : 'kürzer'} als zu Beginn (${erst.label}: ${zahl(erst.medianTage)} Tage). ${diff > 0 ? 'Die Wartezeit wird also länger.' : 'Die Wartezeit wird kürzer.'}`;
}

/** Fazit der Hersteller-/Artikel-Karte: die auffälligste belastbare Zeile. */
export function fazitGruppen(sichtbar: GruppenQuote[], einheit: 'Hersteller' | 'Artikel'): string | null {
  if (sichtbar.length === 0) return null;
  const top = sichtbar[0];
  if (top.quote.anteil === null) return null;
  return `Am häufigsten verspätet: ${top.bezeichnung} — ${anz(top.verspaetet, 'Auftrag', 'Aufträge')} von ${zahl(top.auftraege)} kam${top.verspaetet === 1 ? '' : 'en'} später als vom Lieferanten zuerst geplant (${prozent(top.quote.anteil)}).`
    + (sichtbar.length > 1 && einheit === 'Hersteller' && sichtbar[1].quote.anteil !== null
      ? ` Zum Vergleich: ${sichtbar[1].bezeichnung} liegt bei ${prozent(sichtbar[1].quote.anteil)}.`
      : '');
}

// ─── Perioden: KW / Monat / Jahr + frei waehlbarer Zeitraum ─────────────────
// Wunsch Matze 18.08.2026: "alles auf KW ebene anzeigen bzw einstellbar …
// zeitraeume auch frei waehlbar ab dem ersten erkannten auswertbaren import".
// Deshalb aggregiert das FRONTEND aus den Roh-Faellen (`AnalyticsErgebnis.
// faelle`) — eine fixe Server-Aggregation wuerde die Umschaltbarkeit verbauen.

export type Granularitaet = 'kw' | 'monat' | 'jahr';

/** Von/Bis als Perioden-Keys (inklusiv); null = offen. */
export interface Zeitraum {
  von: string | null;
  bis: string | null;
}

export const GANZER_ZEITRAUM: Zeitraum = { von: null, bis: null };

/** "2026-12" → "2027-01". */
export function naechsterMonat(monat: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(monat);
  if (!m) return monat;
  const jahr = Number(m[1]);
  const nr = Number(m[2]);
  return nr === 12 ? `${jahr + 1}-01` : `${jahr}-${String(nr + 1).padStart(2, '0')}`;
}

/**
 * Wochen-Key → Perioden-Key der Granularitaet. Monat/Jahr einer Woche ist der
 * Monat/das Jahr ihres MONTAGS — eine Woche gehoert ganz in eine Periode.
 * Alle Keys sind als Strings chronologisch sortierbar ("2026-KW34" < "2026-KW35",
 * "2026-08" < "2026-09", "2026" < "2027") — Zeitraum-Filter = String-Vergleich.
 */
export function periodeVonWoche(woche: string, gran: Granularitaet): string {
  if (gran === 'kw') return woche;
  const montag = kwMontag(woche);
  if (!montag) return woche;
  if (gran === 'jahr') return String(montag.getUTCFullYear());
  return `${montag.getUTCFullYear()}-${String(montag.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function periodeLabel(key: string, gran: Granularitaet): string {
  if (gran === 'kw') return kwKurz(key);
  if (gran === 'monat') return monatKurz(key);
  return key;
}

/** Voller Perioden-Name fuer Tooltips ("KW 34/2026", "August 2026", "2026"). */
export function periodeLang(key: string, gran: Granularitaet): string {
  if (gran === 'kw') return kwLang(key);
  if (gran === 'monat') {
    const m = /^(\d{4})-(\d{2})$/.exec(key);
    if (!m) return key;
    const namen = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
    return `${namen[Number(m[2]) - 1] ?? key} ${m[1]}`;
  }
  return key;
}

export function naechstePeriode(key: string, gran: Granularitaet): string {
  if (gran === 'kw') return naechsteWoche(key);
  if (gran === 'monat') return naechsterMonat(key);
  return String(Number(key) + 1);
}

export function inZeitraum(key: string, zeitraum: Zeitraum): boolean {
  if (zeitraum.von !== null && key < zeitraum.von) return false;
  if (zeitraum.bis !== null && key > zeitraum.bis) return false;
  return true;
}

/** Fuellt Perioden-Luecken einer sortierten Reihe mit Leerzeilen auf. */
export function fuellePerioden<T extends { periode: string }>(
  zeilen: T[],
  gran: Granularitaet,
  leer: (periode: string) => T,
  maxLuecken = 200,
): T[] {
  if (zeilen.length < 2) return zeilen;
  const out: T[] = [zeilen[0]];
  for (let i = 1; i < zeilen.length; i++) {
    let cursor = zeilen[i - 1].periode;
    let schutz = 0;
    while (schutz++ < maxLuecken) {
      cursor = naechstePeriode(cursor, gran);
      if (cursor >= zeilen[i].periode || cursor === zeilen[i - 1].periode) break;
      out.push(leer(cursor));
    }
    out.push(zeilen[i]);
  }
  return out;
}

/**
 * Alle waehlbaren Perioden — vom ersten auswertbaren Import bis heute.
 * Basis: jede Woche, in der irgendein Fall ausgeliefert oder umgeplant wurde.
 */
export function verfuegbarePerioden(
  faelle: AnalyticsFall[],
  verschiebungen: VerschiebungPunkt[],
  gran: Granularitaet,
): string[] {
  const wochen = new Set<string>();
  for (const f of faelle) if (f.lieferWoche) wochen.add(f.lieferWoche);
  for (const v of verschiebungen) wochen.add(v.woche);
  const keys = [...new Set([...wochen].map((w) => periodeVonWoche(w, gran)))].sort();
  if (keys.length < 2) return keys;
  // Auch leere Zwischenperioden anbieten — sonst laesst sich "ab KW 28" nicht
  // waehlen, nur weil in KW 28 zufaellig nichts passiert ist.
  const voll: string[] = [keys[0]];
  let schutz = 0;
  while (voll[voll.length - 1] < keys[keys.length - 1] && schutz++ < 400) {
    voll.push(naechstePeriode(voll[voll.length - 1], gran));
  }
  return voll;
}

/** "48" → "48 Tage (6,9 Wo.)" — Matze denkt in Kalenderwochen (18.08.). */
export function tageMitWochen(tage: number | null | undefined): string {
  if (tage === null || tage === undefined || Number.isNaN(tage)) return '—';
  const wochen = (tage / 7).toLocaleString('de-DE', { maximumFractionDigits: 1 });
  return `${zahl(tage)} ${tage === 1 ? 'Tag' : 'Tage'} (${wochen} Wo.)`;
}

export function median(werte: number[]): number | null {
  if (werte.length === 0) return null;
  const s = [...werte].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

// ─── Termintreue je Periode (aus Roh-Faellen) ────────────────────────────────

export interface TreueZeile {
  periode: string;
  label: string;
  puenktlich: number;      // letzte Meldung gehalten (inkl. Toleranz)
  spaet1: number;
  spaet2bis3: number;
  spaet4plus: number;
  gesamt: number;          // messbare Faelle (letzte Meldung bekannt)
  quoteLetzte: number | null;
  ersteGehalten: number;
  ersteMessbar: number;
  quoteErste: number | null;
  duenn: boolean;          // gesamt < 10
}

function leereTreueZeile(periode: string, gran: Granularitaet): TreueZeile {
  return {
    periode, label: periodeLabel(periode, gran),
    puenktlich: 0, spaet1: 0, spaet2bis3: 0, spaet4plus: 0, gesamt: 0,
    quoteLetzte: null, ersteGehalten: 0, ersteMessbar: 0, quoteErste: null, duenn: true,
  };
}

/**
 * Klassifiziert jeden gelieferten Fall gegen seine LETZTE Meldung (mit
 * Erfassungs-Toleranz) und zaehlt daneben, wie oft schon die ERSTE Meldung
 * hielt. Fuehrende Perioden ohne einen einzigen messbaren Fall werden
 * abgeschnitten, innere Luecken aufgefuellt.
 */
export function baueTreueZeilen(
  faelle: AnalyticsFall[],
  gran: Granularitaet,
  zeitraum: Zeitraum,
  toleranz: number,
): TreueZeile[] {
  const map = new Map<string, TreueZeile>();
  for (const f of faelle) {
    if (!f.lieferWoche) continue;
    const periode = periodeVonWoche(f.lieferWoche, gran);
    if (!inZeitraum(periode, zeitraum)) continue;
    const z = map.get(periode) ?? leereTreueZeile(periode, gran);

    if (f.verzugLetzte !== null) {
      const verzug = Math.max(0, f.verzugLetzte - toleranz);
      if (verzug === 0) z.puenktlich++;
      else if (verzug === 1) z.spaet1++;
      else if (verzug <= 3) z.spaet2bis3++;
      else z.spaet4plus++;
      z.gesamt++;
    }
    if (f.verzugErste !== null) {
      z.ersteMessbar++;
      if (Math.max(0, f.verzugErste - toleranz) === 0) z.ersteGehalten++;
    }
    map.set(periode, z);
  }

  const zeilen = [...map.values()]
    .map((z) => ({
      ...z,
      quoteLetzte: z.gesamt > 0 ? z.puenktlich / z.gesamt : null,
      quoteErste: z.ersteMessbar > 0 ? z.ersteGehalten / z.ersteMessbar : null,
      duenn: z.gesamt < 10,
    }))
    .sort((a, b) => a.periode.localeCompare(b.periode));

  const ersterIndex = zeilen.findIndex((z) => z.gesamt > 0);
  const zugeschnitten = ersterIndex <= 0 ? zeilen : zeilen.slice(ersterIndex);
  return fuellePerioden(zugeschnitten, gran, (periode) => leereTreueZeile(periode, gran));
}

/** Fazit der Treue-Karte — aus den ANGEZEIGTEN Zeilen, nicht aus einem fixen Fenster. */
export function fazitTreue(zeilen: TreueZeile[], toleranz: number): string | null {
  const gesamt = zeilen.reduce((s, z) => s + z.gesamt, 0);
  if (gesamt === 0) return 'Im gewählten Zeitraum wurde kein Auftrag mit gemeldetem Termin ausgeliefert.';
  const gehalten = zeilen.reduce((s, z) => s + z.puenktlich, 0);
  const spaeter = gesamt - gehalten;
  const ersteMessbar = zeilen.reduce((s, z) => s + z.ersteMessbar, 0);
  const ersteGehalten = zeilen.reduce((s, z) => s + z.ersteGehalten, 0);
  let satz = `Im gewählten Zeitraum wurden ${zahl(gesamt)} Aufträge mit gemeldetem Liefertermin ausgeliefert. `
    + `${zahl(gehalten)} (${prozent(gehalten / gesamt)}) kamen in der zuletzt gemeldeten Woche an`
    + (spaeter > 0 ? `, ${zahl(spaeter)} kamen später.` : '.');
  if (ersteMessbar > 0 && ersteGehalten / ersteMessbar < gehalten / gesamt - 0.15) {
    satz += ` Die ERSTE Terminangabe hielt nur bei ${zahl(ersteGehalten)} von ${zahl(ersteMessbar)} (${prozent(ersteGehalten / ersteMessbar)}) — die Planung wird also unterwegs korrigiert.`;
  }
  if (gesamt < 10) satz += ` Achtung: erst ${zahl(gesamt)} Fälle — noch keine belastbare Zahl.`;
  return satz;
}

// ─── 6-Wochen-Ziel (AB → Warenausgang) ───────────────────────────────────────
// "ich muss wissen wieviel die 6 Wochen gerissen haben" (Matze 18.08.).
// Dieselbe Schwelle wie die Warn-Spalte des Imports (zielVorlaufTage, 42 Tage).
//
// ERSATZ-UHR OHNE AB (Entscheidung Matze 18.08., zweiter Teil): fehlt die
// Auftragsbestaetigung (bei Sporttore mehr als die Haelfte der Lieferungen),
// zaehlt das Plenty-Auftragsdatum. Es liegt kurz VOR der AB — die Messung ist
// damit minimal strenger, aber die kundenehrliche Wartezeit. In Tabelle und
// Fazit wird der Ersatz gekennzeichnet, nie stillschweigend vermischt.

export interface ZielBasis {
  /** Tage bis zum Montag der Lieferwoche (geliefert) bzw. bis heute (offen). */
  tage: number;
  /** Woraus die Uhr gespeist wurde. */
  quelle: 'ab' | 'bestellung';
  datum: string;
}

/** Start-Uhr eines Falls: AB-Datum, sonst Auftragsdatum. */
export function zielBasisVon(f: AnalyticsFall, bisIso: string | Date): ZielBasis | null {
  // vorlaufTage ist die Backend-Rechnung AB → Montag der Lieferwoche; negativ = Datenfehler.
  if (f.abDatum && f.vorlaufTage !== null && f.vorlaufTage >= 0) {
    return { tage: f.vorlaufTage, quelle: 'ab', datum: f.abDatum };
  }
  // Kaputte AB (datiert nach der Lieferung) wird verworfen — dann darf nur
  // noch das Auftragsdatum einspringen, nicht dieselbe kaputte AB.
  const abKaputt = f.vorlaufTage !== null && f.vorlaufTage < 0;
  const start = (!abKaputt && f.abDatum) ? f.abDatum : f.auftragsdatum;
  if (!start) return null;
  const bis = typeof bisIso === 'string' ? new Date(bisIso) : bisIso;
  const tage = Math.floor((bis.getTime() - new Date(start).getTime()) / 86_400_000);
  if (tage < 0) return null; // Datenfehler
  return { tage, quelle: start === f.abDatum ? 'ab' : 'bestellung', datum: start };
}

export interface ZielZeile {
  periode: string;
  label: string;
  imZiel: number;
  gerissen: number;
  gesamt: number;          // Faelle mit bekannter AB
  ohneAb: number;          // geliefert, aber keine AB auffindbar
  quoteGerissen: number | null;
}

function leereZielZeile(periode: string, gran: Granularitaet): ZielZeile {
  return { periode, label: periodeLabel(periode, gran), imZiel: 0, gerissen: 0, gesamt: 0, ohneAb: 0, quoteGerissen: null };
}

export function baueZielZeilen(
  faelle: AnalyticsFall[],
  gran: Granularitaet,
  zeitraum: Zeitraum,
  zielTage: number,
): ZielZeile[] {
  const map = new Map<string, ZielZeile>();
  for (const f of faelle) {
    if (!f.lieferWoche || !f.geliefert) continue;
    const periode = periodeVonWoche(f.lieferWoche, gran);
    if (!inZeitraum(periode, zeitraum)) continue;
    const z = map.get(periode) ?? leereZielZeile(periode, gran);
    // Uhr: AB-Datum, sonst Auftragsdatum; ohne beides (oder Datenfehler) → ohneAb.
    const basis = zielBasisVon(f, kwMontag(f.lieferWoche) ?? new Date());
    if (basis === null) z.ohneAb++;
    else {
      z.gesamt++;
      if (basis.tage > zielTage) z.gerissen++;
      else z.imZiel++;
    }
    map.set(periode, z);
  }
  const zeilen = [...map.values()]
    .map((z) => ({ ...z, quoteGerissen: z.gesamt > 0 ? z.gerissen / z.gesamt : null }))
    .sort((a, b) => a.periode.localeCompare(b.periode));
  const ersterIndex = zeilen.findIndex((z) => z.gesamt > 0);
  const zugeschnitten = ersterIndex <= 0 ? zeilen : zeilen.slice(ersterIndex);
  return fuellePerioden(zugeschnitten, gran, (periode) => leereZielZeile(periode, gran));
}

export interface ZielRiss {
  orderId: string;
  /** Startdatum der Uhr (AB- oder Bestelldatum, s. `basis`). */
  abDatum: string | null;
  basis: 'ab' | 'bestellung';
  lieferWoche: string | null;
  tage: number;
  artikel: string[];
  offen: boolean;
}

/** Gelieferte Auftraege im Zeitraum, die das Ziel gerissen haben — fuer die Tabelle. */
export function zielRisse(
  faelle: AnalyticsFall[],
  gran: Granularitaet,
  zeitraum: Zeitraum,
  zielTage: number,
): ZielRiss[] {
  const out: ZielRiss[] = [];
  for (const f of faelle) {
    if (!f.geliefert || !f.lieferWoche) continue;
    if (!inZeitraum(periodeVonWoche(f.lieferWoche, gran), zeitraum)) continue;
    const basis = zielBasisVon(f, kwMontag(f.lieferWoche) ?? new Date());
    if (!basis || basis.tage <= zielTage) continue;
    out.push({
      orderId: f.orderId, abDatum: basis.datum, basis: basis.quelle,
      lieferWoche: f.lieferWoche, tage: basis.tage, artikel: f.artikel, offen: false,
    });
  }
  return out.sort((a, b) => b.tage - a.tage);
}

/** Offene Auftraege, deren AB schon aelter ist als das Ziel — die reissen es sicher. */
export function offeneUeberZiel(faelle: AnalyticsFall[], standIso: string, zielTage: number): ZielRiss[] {
  const out: ZielRiss[] = [];
  for (const f of faelle) {
    if (f.geliefert || f.storniert) continue;
    const basis = zielBasisVon(f, standIso);
    if (!basis || basis.tage <= zielTage) continue;
    out.push({
      orderId: f.orderId, abDatum: basis.datum, basis: basis.quelle,
      lieferWoche: null, tage: basis.tage, artikel: f.artikel, offen: true,
    });
  }
  return out.sort((a, b) => b.tage - a.tage);
}

export function fazitZiel(zeilen: ZielZeile[], offene: ZielRiss[], zielTage: number): string | null {
  const gesamt = zeilen.reduce((s, z) => s + z.gesamt, 0);
  const gerissen = zeilen.reduce((s, z) => s + z.gerissen, 0);
  const wochen = Math.round(zielTage / 7);
  if (gesamt === 0 && offene.length === 0) return null;
  const teile: string[] = [];
  if (gesamt > 0) {
    teile.push(`Von ${zahl(gesamt)} ausgelieferten Aufträgen haben ${zahl(gerissen)} (${prozent(gerissen / gesamt)}) das ${wochen}-Wochen-Ziel gerissen — gemessen ab AB-Datum, ohne AB ab Bestelldatum.`);
  }
  if (offene.length > 0) {
    teile.push(`Dazu ${offene.length === 1 ? 'wartet aktuell 1 offener Auftrag' : `warten aktuell ${zahl(offene.length)} offene Aufträge`} schon jetzt länger als ${zahl(zielTage)} Tage.`);
  }
  return teile.join(' ');
}

// ─── Verschiebungen je Periode (aus den Wochen-Aggregaten der API) ───────────

export interface VerschiebungZeile {
  periode: string;
  label: string;
  nachHinten: number;
  nachVorneNegativ: number;
  nachVorne: number;
  saldo: number;
}

export function baueVerschiebungZeilen(
  punkte: VerschiebungPunkt[],
  gran: Granularitaet,
  zeitraum: Zeitraum,
): VerschiebungZeile[] {
  const map = new Map<string, VerschiebungZeile>();
  for (const p of punkte) {
    const periode = periodeVonWoche(p.woche, gran);
    if (!inZeitraum(periode, zeitraum)) continue;
    const z = map.get(periode) ?? {
      periode, label: periodeLabel(periode, gran), nachHinten: 0, nachVorneNegativ: 0, nachVorne: 0, saldo: 0,
    };
    z.nachHinten += p.nachHinten;
    z.nachVorne += p.nachVorne;
    z.nachVorneNegativ = -z.nachVorne;
    z.saldo = z.nachHinten - z.nachVorne;
    map.set(periode, z);
  }
  const zeilen = [...map.values()].sort((a, b) => a.periode.localeCompare(b.periode));
  // Ruhige Perioden sind eine GUTE Nachricht — als Null-Balken zeigen, nicht weglassen.
  return fuellePerioden(zeilen, gran, (periode) => ({
    periode, label: periodeLabel(periode, gran), nachHinten: 0, nachVorneNegativ: 0, nachVorne: 0, saldo: 0,
  }));
}

// ─── Wartezeit (AB → Warenausgang) je Periode ────────────────────────────────

export interface VorlaufZeile {
  periode: string;
  label: string;
  medianTage: number | null;
  n: number;
}

export function baueVorlaufZeilen(
  faelle: AnalyticsFall[],
  gran: Granularitaet,
  zeitraum: Zeitraum,
): VorlaufZeile[] {
  const map = new Map<string, number[]>();
  for (const f of faelle) {
    if (!f.lieferWoche || f.vorlaufTage === null || f.vorlaufTage < 0) continue;
    const periode = periodeVonWoche(f.lieferWoche, gran);
    if (!inZeitraum(periode, zeitraum)) continue;
    map.set(periode, [...(map.get(periode) ?? []), f.vorlaufTage]);
  }
  const zeilen: VorlaufZeile[] = [...map.entries()]
    .map(([periode, werte]) => ({
      periode, label: periodeLabel(periode, gran), medianTage: median(werte), n: werte.length,
    }))
    .sort((a, b) => a.periode.localeCompare(b.periode));
  return fuellePerioden(zeilen, gran, (periode) => ({
    periode, label: periodeLabel(periode, gran), medianTage: null, n: 0,
  }));
}
